import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  Package,
  Save,
  Plus,
  Calendar,
  User,
  MapPin,
  Weight,
  DollarSign,
  Hash
} from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { listCustomers } from '../utils/db/customers';
import CustomerAutocomplete from '../components/CustomerAutocomplete';
import { getNextAwbNo, createBooking } from '../utils/db/bookings';
import { getProfile } from '../utils/db/profiles';
import { findSector, calculateRate, getRateConfiguration } from '../utils/db/rates';

const BookingEntry = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [modes, setModes] = useState([]);
  const [products, setProducts] = useState([]);
  const [sectors, setSectors] = useState([]); // Store sectors for lookup
  const [nextAwbNo, setNextAwbNo] = useState('000001');
  const [isLoading, setIsLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm();
  const [persistedDate, setPersistedDate] = useState(() => {
    return localStorage.getItem('booking_entry_last_date') || '';
  });
  const [persistedCustomer, setPersistedCustomer] = useState(() => {
    try {
      const raw = localStorage.getItem('booking_entry_last_customer');
      return raw ? JSON.parse(raw) : null; // { id, customer_id, customer_name }
    } catch (_) {
      return null;
    }
  });

  // Store a session-scoped date override (clears on reload/navigation)
  const selectedDateRef = useRef('');

  // Initialize form with customer and today's date (or session override if set)
  useEffect(() => {
    if (user) {
      loadCustomers();
      loadNextAwbNo();
      loadModes();
    }
  }, [user]);

  // Initialize form with customer and today's date (or session override if set)
  useEffect(() => {
    if (persistedCustomer?.id) {
      setValue('customerId', persistedCustomer.id, { shouldValidate: false, shouldDirty: false });
    }
    const today = new Date().toISOString().split('T')[0];
    setValue('date', selectedDateRef.current || today, { shouldValidate: false, shouldDirty: false });

    // Clear any transient cached customer when leaving the page
    return () => {
      try { localStorage.removeItem('booking_entry_last_customer'); } catch (_) { }
    };
  }, [persistedCustomer, setValue]);

  const loadCustomers = async () => {
    try {
      const rows = await listCustomers(user.id);
      setCustomers(rows);
    } catch (e) {
      toast.error('Failed to load customers');
    }
  };

  const loadModes = async () => {
    try {
      const profile = await getProfile(user.id);
      if (profile) {
        if (profile.modes) setModes(profile.modes);
        if (profile.products) setProducts(profile.products);
        if (profile.sectors) setSectors(profile.sectors);
      }
    } catch (e) {
      console.error('Failed to load modes/products', e);
    }
  };

  const loadNextAwbNo = async () => {
    try {
      const next = await getNextAwbNo(user.id);
      setNextAwbNo(next);
    } catch (e) {
      setNextAwbNo('000001');
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      console.log('Form data:', data);
      console.log('User ID:', user.id);

      const payload = {
        ...data,
        awbNo: data.awbNo,
      };

      console.log('Payload being sent:', payload);

      const result = await createBooking(user.id, payload);
      console.log('Booking created successfully:', result);

      // Compute next AWB based on current value (increment trailing digits, preserve padding)
      const incrementAwb = (value) => {
        if (!value) return '';
        const m = String(value).match(/^(.*?)(\d+)$/);
        if (m) {
          const prefix = m[1];
          const digits = m[2];
          const next = (parseInt(digits, 10) + 1).toString().padStart(digits.length, '0');
          return `${prefix}${next}`;
        }
        return `${value}1`;
      };
      const nextAwb = incrementAwb(data.awbNo);

      // Persist the selected date for next booking and keep it in the form
      const usedDate = data.date || new Date().toISOString().split('T')[0];
      localStorage.setItem('booking_entry_last_date', usedDate);
      setPersistedDate(usedDate);

      // Persist selected customer for next booking
      if (data.customerId) {
        const cust = customers.find(c => c.id === data.customerId);
        if (cust) {
          const minimal = { id: cust.id, customer_id: cust.customer_id, customer_name: cust.customer_name };
          localStorage.setItem('booking_entry_last_customer', JSON.stringify(minimal));
          setPersistedCustomer(minimal);
        }
      }

      // Reset form while keeping same date and auto-incremented AWB,
      // and clear Receiver & Shipment values
      reset({
        awbNo: nextAwb,
        date: usedDate,
        customerId: persistedCustomer?.id || data.customerId || '',
        receiverName: '',
        receiverAddress: '',
        pincode: '',
        mode: '',
        type: '',
        weight: '',
        quantity: '',
        amount: '',
        otherCharge: ''
      });

      toast.success(`Booking added successfully! AWB #${payload.awbNo}`);
      // Ensure the date remains as the user selected for the next booking on this page
      try {
        const today = new Date().toISOString().split('T')[0];
        setValue('date', selectedDateRef.current || today, { shouldValidate: false, shouldDirty: false });
      } catch (_) { }
      // Reset preview cache to avoid stale reuse
    } catch (error) {
      console.error('Booking creation error:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      });
      toast.error(`Failed to add booking: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const selectedCustomerId = watch('customerId');
  const selectedCustomer = customers.find(customer => customer.id === selectedCustomerId);

  // Rule based calculation
  const watchedWeight = watch('weight');
  const watchedPincode = watch('pincode');
  const watchedMode = watch('mode');
  const watchedType = watch('type'); // Product Name
  const watchedCustomerId = watch('customerId'); // Customer ID

  React.useEffect(() => {
    const calculate = async () => {
      if (!watchedWeight || !watchedPincode || !watchedMode || !watchedType || !watchedCustomerId) return;

      // Find Product ID from Name
      const product = products.find(p => p.name === watchedType);
      if (!product) return;
      const productId = product.id;

      // Find Sector
      const sectorId = findSector(watchedPincode, sectors);

      if (!sectorId) {
        console.warn('Sector not found for pincode:', watchedPincode);
        return;
      }

      setIsCalculating(true);
      try {
        // Fetch rate for this SPECIFIC customer
        const rateConfig = await getRateConfiguration(user.id, productId, watchedMode, watchedCustomerId);

        if (rateConfig) {
          const amount = calculateRate(rateConfig, rateConfig.rates, sectorId, watchedWeight);

          if (amount !== null) {
            setValue('amount', amount);
          }
        } else {
          console.warn('No rate configuration found for:', { productId, watchedMode, watchedCustomerId });
        }
      } catch (e) {
        console.error("Auto calculation error", e);
      } finally {
        setIsCalculating(false);
      }
    };

    const timer = setTimeout(calculate, 500); // Debounce
    return () => clearTimeout(timer);
  }, [watchedWeight, watchedPincode, watchedMode, watchedType, watchedCustomerId, user.id, products, sectors, setValue]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Booking Entry</h1>
        <p className="text-gray-600">Add new AWB booking details (auto incremented)</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Package className="h-5 w-5 mr-2" />
              Booking Information
            </h3>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AWB Number
                </label>
                <div className="flex items-center">
                  <Hash className="h-5 w-5 text-gray-400 mr-2" />
                  <input
                    type="text"
                    {...register('awbNo', {
                      required: 'AWB number is required',
                      pattern: {
                        value: /^[A-Za-z0-9]+$/,
                        message: 'AWB must contain only letters and numbers'
                      },
                      maxLength: {
                        value: 20,
                        message: 'AWB number must be 20 characters or less'
                      }
                    })}
                    maxLength={20}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono font-bold text-primary-600"
                    placeholder="Enter AWB number"
                  />
                </div>
                {errors.awbNo && (
                  <p className="mt-1 text-sm text-red-600">{errors.awbNo.message}</p>
                )}
              </div>

              <div>
                <CustomerAutocomplete
                  label="Customer *"
                  placeholder="Type customer ID"
                  onChange={(c) => setValue('customerId', c.id, { shouldValidate: true })}
                  value={persistedCustomer?.id}
                  valueLabel={persistedCustomer ? `${persistedCustomer.customer_id} - ${persistedCustomer.customer_name}` : ''}
                  className=""
                  error={errors.customerId?.message}
                />
                <input type="hidden" {...register('customerId', { required: 'Please select a customer' })} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date *
              </label>
              <input
                type="date"
                {...register('date', { required: 'Date is required' })}
                onClick={(e) => { try { if (e.currentTarget.showPicker) e.currentTarget.showPicker(); } catch (_) { } }}
                onChange={(e) => { selectedDateRef.current = e.target.value || ''; }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                defaultValue={''}
              />
              {errors.date && (
                <p className="mt-1 text-sm text-red-600">{errors.date.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Receiver Information */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <User className="h-5 w-5 mr-2" />
              Receiver Information
            </h3>
          </div>
          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Receiver Name *
              </label>
              <input
                type="text"
                {...register('receiverName', {
                  required: 'Receiver name is required',
                  maxLength: {
                    value: 100,
                    message: 'Receiver name must be 100 characters or less'
                  }
                })}
                maxLength={100}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter receiver name"
              />
              {errors.receiverName && (
                <p className="mt-1 text-sm text-red-600">{errors.receiverName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Receiver Address *
              </label>
              <textarea
                {...register('receiverAddress', {
                  required: 'Receiver address is required',
                  maxLength: {
                    value: 200,
                    message: 'Receiver address must be 200 characters or less'
                  }
                })}
                rows={3}
                maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter complete receiver address"
              />
              {errors.receiverAddress && (
                <p className="mt-1 text-sm text-red-600">{errors.receiverAddress.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pincode *
              </label>
              <input
                type="text"
                {...register('pincode', {
                  required: 'Pincode is required',
                  pattern: {
                    value: /^[0-9]{6}$/,
                    message: 'Please enter a valid 6-digit pincode'
                  }
                })}
                maxLength={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter 6-digit pincode"
              />
              {errors.pincode && (
                <p className="mt-1 text-sm text-red-600">{errors.pincode.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Shipment Details */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Package className="h-5 w-5 mr-2" />
              Shipment Details
            </h3>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mode *
                </label>
                <select
                  {...register('mode', {
                    required: 'Please select mode',
                    validate: (v) => (v && v.length <= 20) || 'Mode must be 20 characters or less'
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select Mode</option>
                  {modes.map(mode => (
                    <option key={mode} value={mode}>{mode}</option>
                  ))}
                </select>
                {errors.mode && (
                  <p className="mt-1 text-sm text-red-600">{errors.mode.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type *
                </label>
                <select
                  {...register('type', {
                    required: 'Please select type',
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select Type</option>
                  {products.map(product => (
                    <option key={product.id} value={product.name}>{product.name}</option>
                  ))}
                </select>
                {errors.type && (
                  <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weight (kg) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  {...register('weight', {
                    required: 'Weight is required',
                    min: { value: 0.01, message: 'Weight must be greater than 0' }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter weight in kg"
                />
                {errors.weight && (
                  <p className="mt-1 text-sm text-red-600">{errors.weight.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantity *
                </label>
                <input
                  type="number"
                  min="1"
                  {...register('quantity', {
                    required: 'Quantity is required',
                    min: { value: 1, message: 'Quantity must be at least 1' }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter quantity"
                />
                {errors.quantity && (
                  <p className="mt-1 text-sm text-red-600">{errors.quantity.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount (₹) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  {...register('amount', {
                    required: 'Amount is required',
                    min: { value: 0.01, message: 'Amount must be greater than 0' }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter amount"
                />
                {errors.amount && (
                  <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Other Charges (₹)
                </label>
                <input
                  type="number"
                  step="0.01"
                  {...register('otherCharge', {
                    min: { value: 0, message: 'Other charges cannot be negative' }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter other charges"
                />
                {errors.otherCharge && (
                  <p className="mt-1 text-sm text-red-600">{errors.otherCharge.message}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Customer Information Display */}
        {selectedCustomer && (
          <div className="bg-primary-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-primary-900 mb-2">Selected Customer Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-primary-700 font-medium">Name:</span> {selectedCustomer.customerName}
              </div>
              <div>
                <span className="text-primary-700 font-medium">ID:</span> {selectedCustomer.customerId}
              </div>
              <div>
                <span className="text-primary-700 font-medium">Email:</span> {selectedCustomer.email}
              </div>
              <div>
                <span className="text-primary-700 font-medium">Mobile:</span> {selectedCustomer.mobile}
              </div>
              <div className="md:col-span-2">
                <span className="text-primary-700 font-medium">Address:</span> {selectedCustomer.address}
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isLoading}
            className="flex items-center px-6 py-3 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-5 w-5 mr-2" />
            {isLoading ? 'Saving...' : 'Add Booking'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BookingEntry; 